import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';

function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | WearTwice',
    description: 'Learn how WearTwice collects, uses and protects your personal data when you buy or sell pre-loved fashion in the UAE.',
    path: '/privacy-policy',
  });

  return (
    <>
      <section className="relative w-full h-auto bg-[#47715B] p-8">
        <div className="max-w-3xl mx-auto bg-[#47715B] rounded-lg p-6 text-white">
          <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>

          <div className="content">
            <p className="mb-4">
              This Privacy Policy describes how weartwice.ae (the "Site" or "we") collects, uses,
              and discloses your Personal Information when you visit or make a purchase from the Site.
            </p>

            <h2 className="text-xl font-bold mb-4">CONTACT</h2>
            <p className="mb-4">
              After reviewing this policy, if you have additional questions, want more information
              about our privacy practices, or would like to make a complaint, please contact us by
              e-mail at <a href="mailto:support@weartwice.com" className="underline">support@weartwice.com</a> or by mail
              using the details provided below:
            </p>
            <p className="mb-4">
              IFZA Business Park, Building A2<br />
              Dubai Silicon Oasis, Dubai, PO BOX 342001<br />
              United Arab Emirates
            </p>

            <h2 className="text-xl font-bold mb-4">COLLECTING PERSONAL INFORMATION</h2>
            <p className="mb-4">
              When you visit the Site, we collect certain information about your device, your
              interaction with the Site, and information necessary to process your purchases. We may
              also collect additional information if you contact us for customer support. In this
              Privacy Policy, we refer to any information about an identifiable individual (including
              the information below) as "Personal Information." See the list below for more
              information about what Personal Information we collect and why.
            </p>

            <h3 className="text-lg font-bold mb-4">Device information</h3>
            <p className="mb-2"><strong>Purpose of collection:</strong> To load the Site accurately for you, and to perform analytics on Site usage to optimize our Site.</p>
            <p className="mb-2"><strong>Source of collection:</strong> Collected automatically when you access our Site using cookies, log files, web beacons, tags, or pixels.</p>
            <p className="mb-2"><strong>Disclosure for a business purpose:</strong> Shared with our processor Mamo.</p>
            <p className="mb-4">
              <strong>Personal Information collected:</strong> Version of web browser, IP address,
              time zone, cookie information, what sites or products you view, search terms, and how
              you interact with the Site.
            </p>

            <h3 className="text-lg font-bold mb-4">Order information</h3>
            <p className="mb-2">
              <strong>Purpose of collection:</strong> To provide products or services to you to
              fulfill our contract, process your payment information, arrange for shipping, provide
              invoices and/or order confirmations, communicate with you, screen orders for potential
              risk or fraud, and, when in line with the preferences you have shared with us, provide
              you with information or advertising relating to our products or services.
            </p>
            <p className="mb-2"><strong>Source of collection:</strong> Collected from you.</p>
            <p className="mb-2"><strong>Disclosure for a business purpose:</strong> Shared with our processor Mamo and our shipping provider AJEX.</p>
            <p className="mb-6">
              <strong>Personal Information collected:</strong> Name, billing address, shipping
              address, payment information (including credit card numbers), email address, and phone number.
            </p>

            <h2 className="text-xl font-bold mb-4">SHARING PERSONAL INFORMATION</h2>
            <p className="mb-4">
              We share your Personal Information with service providers to help us provide our
              services and fulfill our contracts with you, as described above. For example:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                We use Mamo to power our online store. You can read more about how Mamo uses your
                Personal Information <a href="https://www.mamopay.com/legal/privacy" className="underline" target="_blank" rel="noopener noreferrer">here</a>.
              </li>
              <li>
                We use Ajex to deliver your orders. You can read more about how Ajex uses your
                Personal Information <a href="https://aj-ex.com/privacy-policy" className="underline" target="_blank" rel="noopener noreferrer">here</a>.
              </li>
            </ul>
            <p className="mb-4">
              We may share your Personal Information to comply with applicable laws and regulations,
              respond to a subpoena, search warrant, or other lawful requests for information we
              receive, or to otherwise protect our rights.
            </p>
            <p className="mb-6">
              We only share necessary personal details, such as name and address, with our delivery
              partner Shyft for the shipping of products from seller to buyer.
            </p>

            <h2 className="text-xl font-bold mb-4">BEHAVIOURAL ADVERTISING</h2>
            <p className="mb-4">
              As described above, we use your Personal Information to provide you with targeted
              advertisements or marketing communications that may interest you. For example:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>
                We use Google Analytics to help us understand how our customers use the Site. You
                can read more about how Google uses your Personal Information{' '}
                <a href="https://www.google.com/intl/en/policies/privacy/" className="underline" target="_blank" rel="noopener noreferrer">here</a>.
                You can also opt out of Google Analytics{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" className="underline" target="_blank" rel="noopener noreferrer">here</a>.
              </li>
              <li>
                We use Mamo Audiences to help us show ads on other websites with our advertising
                partners to buyers who made purchases with other Mamo merchants and who may also be
                interested in what we have to offer. We also share information about your use of the
                Site, your purchases, and the email address associated with your purchases with Mamo Audiences.
              </li>
            </ul>

            <h2 className="text-xl font-bold mb-4">COOKIES</h2>
            <p className="mb-4">
              A cookie is a small amount of information that's downloaded to your computer or device
              when you visit our Site. We use a number of different cookies, including functional,
              performance, advertising, and social media or content cookies.
            </p>

            <h3 className="text-lg font-bold mb-4">List of our cookies:</h3>
            <ul className="list-disc pl-6 mb-6">
              <li>
                <strong>Essential/Functional Cookies:</strong>
                <ul className="list-disc pl-6">
                  <li><strong>Session Cookies:</strong> Used to manage the user's session state.</li>
                  <li><strong>Preference Cookies:</strong> Remember user preferences.</li>
                </ul>
              </li>
              <li>
                <strong>Performance Cookies:</strong>
                <ul className="list-disc pl-6">
                  <li><strong>Analytics Cookies:</strong> Used to analyze how users interact with the site.</li>
                </ul>
              </li>
              <li>
                <strong>Advertising Cookies:</strong>
                <ul className="list-disc pl-6">
                  <li><strong>Targeting Cookies:</strong> Used to deliver relevant ads to users.</li>
                </ul>
              </li>
            </ul>

            <h2 className="text-xl font-bold mb-4">YOUR RIGHTS</h2>
            <p className="mb-6">
              If you are a European resident, you have the right to access the personal information
              we hold about you and to ask that your personal information be corrected, updated, or
              deleted. If you would like to exercise this right, please contact us through the
              contact information below.
            </p>

            <h2 className="text-xl font-bold mb-4">DATA RETENTION</h2>
            <p className="mb-6">
              When you place an order through the Site, we will maintain your Order Information for
              our records unless and until you ask us to delete this information.
            </p>

            <h2 className="text-xl font-bold mb-4">CHANGES</h2>
            <p className="mb-6">
              We may update this privacy policy from time to time to reflect, for example, changes
              to our practices or for other operational, legal, or regulatory reasons.
            </p>

            <h2 className="text-xl font-bold mb-4">CONTACT US</h2>
            <p className="mb-4">
              For more information about our privacy practices, if you have questions, or if you
              would like to make a complaint, please contact us by e-mail at{' '}
              <a href="mailto:support@weartwice.com" className="underline">support@weartwice.com</a> or by mail using the
              details provided above.
            </p>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <Link
            to="/"
            className="bg-white text-[#47715B] px-4 py-2 rounded-lg shadow hover:bg-gray-200 transition duration-300"
          >
            Back to Home
          </Link>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Privacy;
